import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Chip,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconUsersPlus, IconX } from "@tabler/icons-react";
import "./InviteTeamModal.scss";
import MemberInviteBanner from "@/components/Tax Pro Only/Members/MemberCapacityBanner/MemberInviteBanner/MemberInviteBanner";
import type { Member } from "./types";
import { useAuthStore } from "@utils/stores/authStore.ts";

interface NewUser {
  email: string;
  role: string;
  autoAllocateCredits?: number;
}

interface InviteTeamModalProps {
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
  onInvite: (users: NewUser[]) => void;
  remainingSeats: number;
  membersList: Member[];
  defaultRole?: string;
  lockRole?: boolean;
}

interface NewUserChipProps {
  user: NewUser;
  setUsers: React.Dispatch<React.SetStateAction<NewUser[]>>;
}

function NewUserChip({ user, setUsers }: NewUserChipProps) {
  const handleRemove = () => {
    setUsers((users) => users.filter((u) => u.email !== user.email));
  };

  return (
    <Chip size="sm" checked={false}>
      <Group gap="xs" style={{ marginLeft: -8, marginRight: -8 }}>
        <Tooltip label={user.email}>
          <Text size="sm" style={{ maxWidth: 300 }} truncate>
            {user.email}
          </Text>
        </Tooltip>
        <Tooltip label="Remove email">
          <ActionIcon
            variant="subtle"
            size="sm"
            color="red"
            onClick={handleRemove}
          >
            <IconX size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Chip>
  );
}

const InviteTeamModal = ({
  opened,
  setOpened,
  onInvite,
  remainingSeats,
  membersList,
  defaultRole,
  lockRole: _lockRole = false,
}: InviteTeamModalProps) => {
  const [users, setUsers] = useState<NewUser[]>([]);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>("");
  const [autoAllocateCredits, setAutoAllocateCredits] = useState<
    number | undefined
  >(undefined);
  const { isOwner } = useAuthStore();
  const [selectedRole, setSelectedRole] = useState(
    defaultRole || "Team Member"
  );

  /**
   * uses non-backtracking regex to validate email addresses
   * @param email a string representing an email address
   * @returns a boolean indicating whether the email is valid or not
   */
  const validateEmail = (email: string): boolean => {
    if (email.length > 320) return false; // RFC 3696 says 320 is max length
    const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

    if (!emailRegex.test(email)) return false;

    const [, domain] = email.split("@");
    if (!domain.includes(".")) return false;

    return true;
  };

  const processEmails = (emailString: string) => {
    const emailList = emailString.split(",");
    const newUsers: NewUser[] = [];
    const invalidEmails: string[] = [];
    const duplicateEmails: string[] = [];

    emailList.forEach((email) => {
      const trimmedEmail = email.trim();
      if (trimmedEmail) {
        if (validateEmail(trimmedEmail)) {
          // Check if email exists in members list with active/pending status
          const isExistingMember = membersList.some(
            (member) =>
              member.email.toLowerCase() === trimmedEmail.toLowerCase() &&
              (member.status === "active" || member.status === "pending")
          );

          if (isExistingMember) {
            duplicateEmails.push(trimmedEmail);
          } else if (!users.find((user) => user.email === trimmedEmail)) {
            newUsers.push({
              email: trimmedEmail,
              role: selectedRole,
            });
          }
        } else {
          invalidEmails.push(trimmedEmail);
        }
      }
    });

    setUsers((prevUsers) => [...prevUsers, ...newUsers]);
    setInputValue("");

    // Build combined error message
    const errorMessages = [];
    if (invalidEmails.length > 0) {
      errorMessages.push(`Invalid email: ${invalidEmails.join(", ")}`);
    }
    if (duplicateEmails.length > 0) {
      errorMessages.push(`User already exists: ${duplicateEmails.join(", ")}`);
    }

    setError(errorMessages.length > 0 ? errorMessages.join("; ") : null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    if (value.includes(",")) {
      processEmails(value);
    }
  };

  const handleInvite = () => {
    if (users.length > 0) {
      // Add auto-allocate credits to all users if specified
      const usersWithCredits = users.map((user) => ({
        ...user,
        autoAllocateCredits:
          autoAllocateCredits && autoAllocateCredits > 0
            ? autoAllocateCredits
            : undefined,
      }));
      onInvite(usersWithCredits);
      setOpened(false);
      setUsers([]);
      setAutoAllocateCredits(undefined);
    } else {
      console.error("No users have been entered");
    }
  };

  const isSendInviteDisabled = users.length === 0 || remainingSeats <= 0;

  const clearAll = () => {
    setOpened(false);
    setUsers([]);
    setInputValue("");
    setError(null);
    setAutoAllocateCredits(undefined);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (defaultRole) {
      setSelectedRole(defaultRole);
    }
  }, [opened, defaultRole]);

  // Update all existing users' roles when the role selector changes
  useEffect(() => {
    setUsers((prevUsers) =>
      prevUsers.map((user) => ({ ...user, role: selectedRole }))
    );
  }, [selectedRole]);

  useEffect(() => {
    if (!isFocused && inputValue) {
      processEmails(inputValue);
    }
  }, [isFocused]);

  const getDisabledReason = () => {
    if (users.length === 0) return "Please enter at least one email address.";
    if (remainingSeats <= 0) return "You have reached your team capacity.";
    return null;
  };

  const disabledReason = getDisabledReason();

  return (
    <Modal
      opened={opened}
      radius="md"
      centered
      className="invite-team-modal"
      onClose={() => setOpened(false)}
      size="lg"
      title={
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <IconUsersPlus stroke={1.5} />
          <h2 style={{ margin: 0 }}>Invite your team</h2>
        </span>
      }
      withCloseButton={false}
    >
      <MemberInviteBanner remainingSeats={remainingSeats} />

      <Stack gap="md" mt="lg">
        <Text size="sm">
          Enter email addresses separated by commas to invite{" "}
          <Text span fw={600} c="blue">
            {selectedRole === "Team Lead" ? "Team Leads" : "Team Members"}
          </Text>
        </Text>
        <Group justify="center">
          <Badge
            size="xl"
            variant="light"
            color={selectedRole === "Team Lead" ? "blue" : "green"}
            style={{ fontSize: "16px", padding: "12px 24px" }}
          >
            {selectedRole}
          </Badge>
        </Group>
      </Stack>
      <Group justify="space-between" align="center" mt="md" mb={5}>
        <p className="email-addresses">Email Addresses</p>
        {users.length > 0 && (
          <Button
            variant="subtle"
            size="compact-sm"
            color="red"
            rightSection={<IconX className="iconx" size={16} />}
            onClick={() => setUsers([])}
          >
            Clear All
          </Button>
        )}
      </Group>
      <div className="email-box">
        <div className="email-container">
          {users.length > 0 && (
            <Stack my="sm" gap="xs">
              {users.map((user) => (
                <NewUserChip key={user.email} user={user} setUsers={setUsers} />
              ))}
            </Stack>
          )}
        </div>
        <TextInput
          className="email-input"
          placeholder="Type or paste email addresses, separated by commas"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => e.key === "Enter" && setIsFocused(false)}
          ref={inputRef}
          error={error}
        />
      </div>

      {isOwner() && (
        <div style={{ marginTop: "20px" }}>
          <NumberInput
            label="Auto-allocate credits"
            description="Specify the number of credits to automatically allocate to each new team member when they create their account"
            placeholder="Enter credits amount"
            value={autoAllocateCredits}
            onChange={(value) =>
              setAutoAllocateCredits(
                typeof value === "number" ? value : undefined
              )
            }
            min={0}
            step={1}
            allowNegative={false}
            allowDecimal={false}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "20px",
        }}
      >
        <Button variant="outline" onClick={clearAll}>
          Cancel
        </Button>
        <Tooltip
          label={disabledReason ?? "Click to send invite"}
          disabled={!isSendInviteDisabled}
        >
          <Button disabled={isSendInviteDisabled} onClick={handleInvite}>
            Send Invite
          </Button>
        </Tooltip>
      </div>
    </Modal>
  );
};

export default InviteTeamModal;
